import { useEffect, useMemo, useState } from "react";
import { fetchXReport, sendXReportWhatsapp } from "@/lib/api";

const fmt = (n) => `£${Number(n || 0).toFixed(2)}`;
const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * X-report modal — internal end-of-shift snapshot.
 * Master-admin only. Shows per-brand gross + a total summary with full fee breakdown.
 */
export const XReportModal = ({ onClose }) => {
    const [fromDate, setFromDate] = useState(todayISO());
    const [toDate, setToDate] = useState(todayISO());
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    // WhatsApp send
    const [waOpen, setWaOpen] = useState(false);
    const [waTo, setWaTo] = useState(() => {
        try { return localStorage.getItem("naami_xreport_wa_to") || ""; } catch { return ""; }
    });
    const [waSending, setWaSending] = useState(false);
    const [waResult, setWaResult] = useState(null);

    const load = (from, to) => {
        setLoading(true);
        setErr("");
        // Only pass end_date to backend when it's a real range — a single-day
        // pull keeps the legacy shape and lets the WhatsApp send behave the same.
        const endDate = (to && to !== from) ? to : null;
        fetchXReport(from, endDate)
            .then(setReport)
            .catch((e) => setErr(e.response?.data?.detail || "Could not load X-report"))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(fromDate, toDate); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

    const generatedAt = useMemo(() => {
        if (!report?.generated_at) return "";
        try { return new Date(report.generated_at).toLocaleString("en-GB", { hour12: false }); }
        catch { return report.generated_at; }
    }, [report?.generated_at]);

    const isRange = report && report.from_date && report.to_date && report.from_date !== report.to_date;
    const rangeLabel = report
        ? (isRange ? `${report.from_date} → ${report.to_date}` : (report.from_date || report.date))
        : "";

    const printReport = () => {
        const w = window.open("", "_blank");
        if (!w) { alert("Pop-up blocked. Allow pop-ups to print / save PDF."); return; }
        w.document.write(buildPrintHTML(report));
        w.document.close();
    };

    const sendWhatsapp = async () => {
        const to = (waTo || "").trim();
        if (!to) { setWaResult({ error: "Enter a recipient phone number (E.164, e.g. +447902139373)" }); return; }
        try { localStorage.setItem("naami_xreport_wa_to", to); } catch { /* ignore */ }
        setWaSending(true);
        setWaResult(null);
        try {
            // WhatsApp share intentionally uses the FROM date only — the linked
            // report page renders single-day today; a range summary link is a
            // future enhancement.
            const r = await sendXReportWhatsapp(to, fromDate);
            setWaResult(r);
        } catch (e) {
            setWaResult({ error: e.response?.data?.detail || "Failed to send WhatsApp" });
        } finally { setWaSending(false); }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-[var(--bg)]/95 backdrop-blur grid place-items-center p-6"
            onClick={onClose}
            data-testid="x-report-modal-backdrop"
        >
            <div
                className="bg-[var(--surface)] border border-[var(--border)] max-w-3xl w-full max-h-[92vh] overflow-y-auto p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-5">
                    <div>
                        <p className="eyebrow mb-1">// X-Report · {isRange ? "Date range" : "End of shift"}</p>
                        <h2 className="font-display text-[32px] leading-[0.95] text-gold">
                            {isRange ? "Range take." : "Today\u2019s take."}
                        </h2>
                        {rangeLabel && (
                            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted mt-1">
                                {rangeLabel}{report?.day_count > 1 ? ` · ${report.day_count} days` : ""}
                            </p>
                        )}
                        {generatedAt && (
                            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mt-1">
                                Generated {generatedAt}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hover:text-gold"
                        data-testid="x-report-close"
                    >
                        ✕ Close
                    </button>
                </div>

                {/* Date range picker + PDF + whatsapp */}
                <div className="flex flex-wrap items-center gap-3 mb-4 pb-5 border-b border-[var(--border)]">
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">From</label>
                    <input
                        type="date"
                        className="input max-w-[170px]"
                        value={fromDate}
                        onChange={(e) => {
                            const v = e.target.value;
                            setFromDate(v);
                            // Keep `to` >= `from` — nudge if the user picks a range end before the range start.
                            if (toDate && v > toDate) setToDate(v);
                        }}
                        data-testid="x-report-from-date"
                    />
                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">To</label>
                    <input
                        type="date"
                        className="input max-w-[170px]"
                        value={toDate}
                        min={fromDate}
                        onChange={(e) => setToDate(e.target.value)}
                        data-testid="x-report-to-date"
                    />
                    <button onClick={() => load(fromDate, toDate)} className="btn-secondary" data-testid="x-report-refresh">
                        ↻ Refresh
                    </button>
                    <button onClick={printReport} className="btn-secondary" disabled={!report} data-testid="x-report-print" title="Opens the print dialog — choose 'Save as PDF' from the destination list to export.">
                        🖨 Print / PDF
                    </button>
                    <button onClick={() => setWaOpen((v) => !v)} className="btn-primary" disabled={!report} data-testid="x-report-whatsapp-open">
                        📲 Send to WhatsApp
                    </button>
                </div>

                {waOpen && (
                    <div className="border border-[var(--gold)] bg-[var(--surface-elevated)] p-4 mb-5" data-testid="x-report-whatsapp-panel">
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold mb-2">// WhatsApp send</p>
                        <p className="text-muted text-[12px] mb-3 font-light leading-snug">
                            Sends a short summary + a 48h share-link to the report HTML. Multiple recipients comma-separated.
                            Daily auto-send fires at 23:15 Europe/London — recipient configurable via <span className="font-mono">XREPORT_WHATSAPP_TO</span>.
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            <input
                                className="input flex-1 min-w-[220px]"
                                placeholder="+447902139373"
                                value={waTo}
                                onChange={(e) => setWaTo(e.target.value)}
                                data-testid="x-report-whatsapp-to"
                            />
                            <button
                                onClick={sendWhatsapp}
                                disabled={waSending || !waTo.trim()}
                                className="btn-primary disabled:opacity-40"
                                data-testid="x-report-whatsapp-send"
                            >
                                {waSending ? "Sending…" : "📤 Send now"}
                            </button>
                        </div>
                        {waResult && (
                            <div className="text-[12px] font-mono leading-relaxed" data-testid="x-report-whatsapp-result">
                                {waResult.error && (
                                    <p className="text-[#ff5246]">✕ {waResult.error}</p>
                                )}
                                {!waResult.error && waResult.any_sent && (
                                    <p className="text-gold">✓ Sent to {waResult.results?.length || 0} recipient(s).</p>
                                )}
                                {!waResult.error && waResult.any_sent === false && (
                                    <div className="space-y-1">
                                        <p className="text-[#ff8c42]">⚠ Not delivered. Reasons:</p>
                                        {(waResult.results || []).map((r, i) => (
                                            <p key={i} className="text-muted ml-3">
                                                {r.to}: {r.reason || (r.mocked ? "Twilio not configured" : "Unknown")}
                                            </p>
                                        ))}
                                        {(waResult.results || []).some((r) => r.mocked) && (
                                            <p className="text-muted mt-2 text-[11px]">
                                                Add <span className="text-gold">TWILIO_ACCOUNT_SID</span>, <span className="text-gold">TWILIO_AUTH_TOKEN</span>, <span className="text-gold">TWILIO_WHATSAPP_FROM</span> to backend env to enable real sending.
                                            </p>
                                        )}
                                    </div>
                                )}
                                {waResult.public_url && (
                                    <p className="text-muted mt-2 break-all">
                                        Share link: <a href={waResult.public_url} target="_blank" rel="noreferrer" className="text-gold underline">{waResult.public_url}</a>
                                    </p>
                                )}
                                {waResult.message_preview && (
                                    <details className="mt-2">
                                        <summary className="text-muted cursor-pointer text-[11px]">Message preview</summary>
                                        <pre className="whitespace-pre-wrap bg-[var(--surface)] border border-[var(--border)] p-3 mt-2 text-[11px]">{waResult.message_preview}</pre>
                                    </details>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {loading && <p className="text-muted font-mono text-[12px]">Loading…</p>}
                {err && <p className="font-mono text-[12px] text-[#ff5246]" data-testid="x-report-error">{err}</p>}

                {report && !loading && (
                    <>
                        {/* Per-brand table */}
                        <p className="eyebrow mb-3">// Per brand{isRange ? ` (${report.day_count} days)` : ""}</p>
                        {report.brands.length === 0 ? (
                            <p className="text-muted font-mono text-[12px] mb-6">
                                No paid orders {isRange ? "in this date range." : "on this date."}
                            </p>
                        ) : (
                            <div className="border border-[var(--border)] mb-6" data-testid="x-report-brands">
                                <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-[var(--surface-elevated)] border-b border-[var(--border)] font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                                    <div className="col-span-5">Brand</div>
                                    <div className="col-span-2 text-right">Orders</div>
                                    <div className="col-span-2 text-right">Items</div>
                                    <div className="col-span-3 text-right">Gross</div>
                                </div>
                                {report.brands.map((b) => (
                                    <div
                                        key={b.brand_id}
                                        className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[var(--border)] last:border-b-0 items-center"
                                        data-testid={`x-report-brand-${b.brand_token}`}
                                    >
                                        <div className="col-span-5 flex items-center gap-3">
                                            <span className="logo-box" style={{ width: 28, height: 28, fontSize: 11 }}>{b.brand_token}</span>
                                            <span className="font-display text-[15px]">{b.brand_name}</span>
                                        </div>
                                        <div className="col-span-2 text-right font-mono text-[13px]">{b.order_count}</div>
                                        <div className="col-span-2 text-right font-mono text-[13px]">{b.items_sold}</div>
                                        <div className="col-span-3 text-right font-mono text-[13px] text-gold">{fmt(b.gross)}</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Total card */}
                        <p className="eyebrow mb-3">// Total (all brands)</p>
                        <div className="border-2 border-[var(--gold)] p-5 bg-[var(--surface-elevated)]" data-testid="x-report-total">
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <Stat label="Paid orders" value={report.total.order_count} mono />
                                <Stat label="Items sold" value={report.total.items_sold} mono />
                            </div>

                            <div className="space-y-1 mb-4 font-mono text-[12px]">
                                <Row label="Brand gross (subtotals)" value={fmt(report.total.brand_gross)} />
                                <Row label="− Discounts &amp; codes" value={`−${fmt(report.total.discounts)}`} muted />
                                <Row label="+ Delivery fees" value={fmt(report.total.delivery_fees)} muted />
                                <Row label="+ Service fees" value={fmt(report.total.service_fees)} muted />
                                <Row label="+ Small-order fees" value={fmt(report.total.small_order_fees)} muted />
                                <Row label="+ Bag charges" value={fmt(report.total.bag_charges)} muted />
                            </div>

                            <div className="border-t border-[var(--gold)] pt-3 flex items-baseline justify-between">
                                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted">
                                    Charged to till
                                </span>
                                <span className="font-display text-[28px] text-gold" data-testid="x-report-charged">
                                    {fmt(report.total.charged)}
                                </span>
                            </div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mt-2 text-right">
                                Avg basket {fmt(report.total.avg_basket)}
                            </p>
                        </div>

                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mt-4">
                            Snapshot only — does not zero out counters. Paid (non-cancelled) orders only.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

const Stat = ({ label, value, mono }) => (
    <div className="border border-[var(--border)] p-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mb-1">{label}</div>
        <div className={`${mono ? "font-mono" : "font-display"} text-[22px]`}>{value}</div>
    </div>
);

const Row = ({ label, value, muted }) => (
    <div className="flex justify-between items-center">
        <span className={muted ? "text-muted" : ""}>{label}</span>
        <span className={muted ? "text-muted" : "text-gold"}>{value}</span>
    </div>
);

const buildPrintHTML = (report) => {
    if (!report) return "";
    const rows = (report.brands || []).map((b) => `
        <tr>
            <td><strong>${b.brand_token}</strong> ${escapeHtml(b.brand_name)}</td>
            <td style="text-align:right">${b.order_count}</td>
            <td style="text-align:right">${b.items_sold}</td>
            <td style="text-align:right">${fmt(b.gross)}</td>
        </tr>`).join("");
    const t = report.total || {};
    const from = report.from_date || report.date;
    const to = report.to_date || report.date;
    const isRange = from !== to;
    const rangeTitle = isRange ? `${from} → ${to}` : from;
    const dayCount = report.day_count || 1;
    return `<!doctype html><html><head><title>X-Report ${rangeTitle}</title>
        <style>
            @page { size: A4; margin: 18mm; }
            body { font-family: -apple-system, Arial, sans-serif; color: #111; }
            h1 { font-size: 22pt; margin: 0; }
            .meta { color: #666; font-size: 10pt; margin-top: 3mm; margin-bottom: 8mm; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 8mm; }
            th, td { padding: 6px 8px; border-bottom: 1px solid #ddd; font-size: 11pt; }
            th { text-align: left; background: #f7f7f7; font-size: 9pt; text-transform: uppercase; letter-spacing: 0.1em; color: #666; }
            tfoot td { font-weight: bold; }
            .totals { border: 2px solid #C8A65A; padding: 6mm; margin-top: 8mm; }
            .totals h2 { margin: 0 0 4mm 0; font-size: 14pt; }
            .totals .grand { font-size: 22pt; color: #C8A65A; font-weight: bold; }
            .row { display: flex; justify-content: space-between; font-size: 11pt; padding: 2px 0; }
            .footer { margin-top: 12mm; font-size: 9pt; color: #888; }
        </style></head><body>
        <h1>X-Report · ${rangeTitle}${isRange ? ` <span style="font-size:12pt;color:#888;font-weight:normal">(${dayCount} days)</span>` : ""}</h1>
        <div class="meta">Generated ${new Date(report.generated_at).toLocaleString("en-GB", { hour12: false })} · Paid orders only · Snapshot (does not zero counters)</div>
        <table>
            <thead><tr><th>Brand</th><th style="text-align:right">Orders</th><th style="text-align:right">Items</th><th style="text-align:right">Gross</th></tr></thead>
            <tbody>${rows || '<tr><td colspan="4" style="text-align:center;color:#888;padding:8mm">No paid orders</td></tr>'}</tbody>
        </table>
        <div class="totals">
            <h2>Total (all brands)</h2>
            <div class="row"><span>Paid orders</span><strong>${t.order_count || 0}</strong></div>
            <div class="row"><span>Items sold</span><strong>${t.items_sold || 0}</strong></div>
            <div class="row"><span>Brand gross (subtotals)</span><strong>${fmt(t.brand_gross)}</strong></div>
            <div class="row"><span>− Discounts &amp; codes</span><span>−${fmt(t.discounts)}</span></div>
            <div class="row"><span>+ Delivery fees</span><span>${fmt(t.delivery_fees)}</span></div>
            <div class="row"><span>+ Service fees</span><span>${fmt(t.service_fees)}</span></div>
            <div class="row"><span>+ Small-order fees</span><span>${fmt(t.small_order_fees)}</span></div>
            <div class="row"><span>+ Bag charges</span><span>${fmt(t.bag_charges)}</span></div>
            <div class="row" style="border-top:1px solid #C8A65A;padding-top:4mm;margin-top:4mm">
                <span>Charged to till</span><span class="grand">${fmt(t.charged)}</span>
            </div>
            <div class="row" style="color:#888"><span>Avg basket</span><span>${fmt(t.avg_basket)}</span></div>
        </div>
        <div class="footer">Naami Kitchens · Internal X-Report</div>
        <script>window.onload=()=>{window.focus();window.print();}</script>
        </body></html>`;
};

const escapeHtml = (s) => String(s || "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export default XReportModal;
